// Public consult-cancellation landing page.  Reached from the cancellation
// URL in the confirmation / reminder email; no auth required.  Two-step:
// (1) load booking metadata from POST /api/public/consult/cancel/lookup,
// (2) show a confirmation button that POSTs to /api/public/consult/cancel.
// On success flips to a "cancelled — sorry to miss you" state.
//
// TOKEN HANDLING — mirrors EngagementSign.js / QuestionnaireFill.js:
// the cancellation token is a bearer credential (anyone holding it can cancel
// the booking), so it rides the URL FRAGMENT (#t=…), never the path.  A path
// or query is written verbatim into Apache's access log (`%r` in the combined
// LogFormat) and leaks through the Referer header to any embedded resource;
// a fragment is never transmitted at all.  We:
//   1. read it from the fragment on mount (fresh email click),
//   2. persist it to sessionStorage (tab-scoped) so a refresh survives,
//   3. history.replaceState-scrub it so it doesn't linger in the address bar,
//   4. send it only in request BODIES.
//
// Endpoint is scoped-rate-limited on the backend so an attacker with a
// harvested token can't do more than ~30 POSTs / 5 min / IP.

import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import api from '../services/api'

const TOKEN_STORAGE_KEY = 'consult_cancel_token'

export default function ConsultCancel () {
  // Legacy links mailed before the fragment move carry the token as a path
  // segment (/consult/cancel/:token).  That form is already logged by the time
  // this component runs — nothing can un-log it — but honouring it keeps a
  // link that is sitting in someone's inbox working, and we immediately move
  // the token to storage and scrub the path so it stops being re-sent.
  const { token: legacyPathToken } = useParams()

  const token = useMemo(() => {
    const raw = window.location.hash || ''
    const m = raw.match(/^#(?:t=)?([A-Za-z0-9_\-.=]+)$/)
    const found = m ? m[1] : (legacyPathToken || '')
    if (found) {
      try { sessionStorage.setItem(TOKEN_STORAGE_KEY, found) } catch { /* private mode */ }
      return found
    }
    try { return sessionStorage.getItem(TOKEN_STORAGE_KEY) || '' } catch { return '' }
  }, [legacyPathToken])

  const [state, setState] = useState('loading')   // loading | ready | cancelling | cancelled | notfound | past | missing | error
  const [booking, setBooking] = useState(null)
  const [error, setError] = useState('')

  // Strip the token out of the address bar / history entry.  Covers both the
  // fragment and the legacy path segment.
  useEffect(() => {
    if (window.location.hash || legacyPathToken) {
      window.history.replaceState(null, '', '/consult/cancel')
    }
  }, [legacyPathToken])

  useEffect(() => {
    let cancelled = false
    if (!token) { setState('missing'); return }
    api.post('/api/public/consult/cancel/lookup', { token })
      .then(r => {
        if (cancelled) return
        setBooking(r.data)
        if (r.data.already_cancelled) setState('cancelled')
        else if (r.data.already_past) setState('past')
        else setState('ready')
      })
      .catch(err => {
        if (cancelled) return
        if (err.response?.status === 404) setState('notfound')
        else { setError(err.response?.data?.error || 'Could not load'); setState('error') }
      })
    return () => { cancelled = true }
  }, [token])

  const doCancel = async () => {
    setState('cancelling')
    try {
      await api.post('/api/public/consult/cancel', { token })
      try { sessionStorage.removeItem(TOKEN_STORAGE_KEY) } catch { /* private mode */ }
      setState('cancelled')
    } catch (err) {
      if (err.response?.status === 410) setState('past')
      else { setError(err.response?.data?.error || 'Cancel failed'); setState('error') }
    }
  }

  const slotDisplay = booking?.slot_starts_at
    ? new Date(booking.slot_starts_at).toLocaleString('en-US', {
        // date-fns `format` renders in the BROWSER's zone.  For a Fri 10:00 PDT
        // booking a viewer in Taipei read "Saturday ... 1:00 AM" — not merely a
        // time shift but the WRONG DAY, on the page a client uses to decide
        // whether to cancel.  The zone now comes from the server (display_tz)
        // rather than a browser global that this public page cannot see.
        timeZone: booking.display_tz || 'America/Los_Angeles',
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      })
    : ''

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Cancel your consultation</h1>

        {state === 'loading' && (
          <p className="text-sm text-gray-500">Loading your booking…</p>
        )}

        {state === 'missing' && (
          <div className="mt-4">
            <p className="text-sm text-gray-700">
              This link is missing the part that identifies your booking.  Please open the
              cancellation link straight from your confirmation email — copying only part of
              the address will drop it.
            </p>
            <p className="text-xs text-gray-500 mt-3">
              If you need to cancel a consultation, please reply to your confirmation email.
            </p>
          </div>
        )}

        {state === 'notfound' && (
          <div className="mt-4">
            <p className="text-sm text-gray-700">
              We couldn't find a booking for this link.  It may already have been cancelled,
              or the link may be incorrect.
            </p>
            <p className="text-xs text-gray-500 mt-3">
              If you need to cancel a consultation, please reply to your confirmation email.
            </p>
          </div>
        )}

        {state === 'past' && (
          <div className="mt-4">
            <p className="text-sm text-gray-700">
              This consultation has already passed and can no longer be cancelled online.
              If you were unable to attend, please reply to your confirmation email so we
              can reschedule.
            </p>
          </div>
        )}

        {state === 'ready' && booking && (
          <div className="mt-4 space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
              {booking.lead_name_masked && (
                <p className="text-gray-800"><strong>{booking.lead_name_masked}</strong></p>
              )}
              <p className="text-gray-800 mt-1">{slotDisplay}</p>
            </div>
            <p className="text-xs text-gray-500">
              We show initials rather than your full name so that this link reveals nothing
              about you if it is forwarded or shared.
            </p>
            <p className="text-sm text-gray-700">
              Click below to cancel this consultation.  You'll receive no further reminders,
              and the time slot will be freed for someone else.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={doCancel}
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg text-sm">
                Cancel consultation
              </button>
              <a href="/" className="text-sm text-gray-500 hover:text-gray-700 self-center">
                Keep my booking
              </a>
            </div>
          </div>
        )}

        {state === 'cancelling' && (
          <p className="text-sm text-gray-600 mt-4">Cancelling…</p>
        )}

        {state === 'cancelled' && (
          <div className="mt-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm">
              <p className="text-emerald-800 font-medium">Your consultation has been cancelled.</p>
              {slotDisplay && (
                <p className="text-emerald-700 mt-1">{slotDisplay} is now open.</p>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              If you'd like to reschedule, reply to your original confirmation email
              or visit our intake form again.
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="mt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error || 'Something went wrong.  Please try again in a minute, or reply to your confirmation email.'}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
